import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Creators.css';
import { getRecommendedProfiles } from '../../../shared/actions/userActions';
import CreatorCardWithRound from '../../../features/cards/CreatorCardWithRound';
import { reportError } from '../../../shared/sentry';

const RecommendedCreators = ({ 
  currentCreatorName = null,
  vatPercentage = 0,
}) => {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollDirection, setScrollDirection] = useState(null);
  const scrollContainerRef = useRef(null);

  const fetchRecommendedCreators = useCallback(async () => {
    try {
      const response = await getRecommendedProfiles({ limit: 9, skip: 0 });

      if (response.success) {
        if(currentCreatorName) {
          const filteredCreators = response.data.filter(creator => creator.username !== currentCreatorName);
          setCreators(filteredCreators || []);
        } else {
          setCreators(response.data || []);
        }
      }
    } catch (error) {
      reportError(error, 'RecommendedCreators fetch');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecommendedCreators();
  }, [fetchRecommendedCreators]);

  const handleFollowSuccess = useCallback((creatorId) => {
    setCreators(prevCreators => prevCreators.filter(creator => creator._id !== creatorId));
  }, []);

  const handleUnfollowSuccess = useCallback((creatorId) => {
    setCreators(prevCreators => 
      prevCreators.map(creator => 
        creator._id === creatorId 
          ? { ...creator, isFollowing: false }
          : creator
      )
    );
  }, []);

  // Handle arrow click to scroll the container
  const handleArrowClick = useCallback((direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Set scroll direction to trigger animation
    setScrollDirection(direction);

    // Scroll the container
    const scrollAmount = 300; // Adjust this value as needed
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }

    // Reset direction after animation completes
    setTimeout(() => {
      setScrollDirection(null);
    }, 600);
  }, []);

  if (loading) {
    return (
      <div className="creators-loading-container">
        <div className="creators-loading-text">
          Loading recommended creators...
        </div>
      </div>
    );
  }

  if (!creators || creators.length === 0) {
    return null;
  }

  return (
    <div className="creators-container d-block">
      <div className="creators-header">
        <h3 className="creators-header-title">Recommended</h3>
      </div>

      <div 
        className="recommended-creators-container" 
        ref={scrollContainerRef}
      >
        {creators.map((creator) => (
          <CreatorCardWithRound
            key={creator._id}
            creator={creator}
            vatPercentage={vatPercentage}
            onFollowSuccess={handleFollowSuccess}
            onUnfollowSuccess={handleUnfollowSuccess}
          />
        ))}
      </div>

      <div className="creators-swipe-indicator">
        <div className="creators-swipe-indicator-inner">
          <span 
            className={`creators-swipe-arrow ${scrollDirection === 'left' ? 'slide-left' : ''}`}
            onClick={() => handleArrowClick('left')}
            style={{ cursor: 'pointer' }}
          >
            <i className='fa fa-chevron-left'></i>
          </span>
          <span>Swipe to see more</span>
          <span 
            className={`creators-swipe-arrow ${scrollDirection === 'right' ? 'slide-right' : ''}`}
            onClick={() => handleArrowClick('right')}
            style={{ cursor: 'pointer' }}
          >
            <i className='fa fa-chevron-right'></i>
          </span>
        </div>
      </div>
    </div>
  );
};

export default RecommendedCreators; 